import { Button, Group, Stack, Text, Textarea } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { MAX_INPUT_CHARS } from "@models/translation";
import { SourceLanguageSelect } from "@features/LanguageSelect";

interface Props {
  sourceLang: string;
  onSourceLangChange: (value: string) => void;
  text: string;
  onTextChange: (value: string) => void;
  onClear: () => void;
  overLimit: boolean;
  disabled?: boolean;
}

export const SourcePanel: React.FC<Props> = ({
  sourceLang,
  onSourceLangChange,
  text,
  onTextChange,
  onClear,
  overLimit,
  disabled,
}) => {
  const { t } = useTranslation();
  const count = text.length;
  const nearLimit = count > MAX_INPUT_CHARS * 0.9;

  return (
    <Stack gap="sm" h="100%" style={{ flex: 1 }}>
      <Group justify="space-between" align="center">
        <Text
          fw={700}
          size="xs"
          tt="uppercase"
          style={{ letterSpacing: "0.06em" }}
          c="dimmed"
        >
          {t("translation.source")}
        </Text>
        <SourceLanguageSelect
          value={sourceLang}
          onChange={onSourceLangChange}
          disabled={disabled}
        />
      </Group>

      <Textarea
        value={text}
        onChange={(e) => onTextChange(e.currentTarget.value)}
        placeholder={t("translation.sourcePlaceholder")}
        aria-label={t("translation.source")}
        autosize
        minRows={7}
        maxRows={14}
        disabled={disabled}
        error={overLimit}
        variant="unstyled"
        styles={{
          root: { flex: 1, display: "flex", flexDirection: "column" },
          wrapper: { flex: 1 },
          input: {
            fontSize: "15px",
            lineHeight: "1.55",
            height: "100%",
          },
        }}
      />

      <Group justify="flex-end">
        {text.length > 0 && (
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            leftSection={<IconX size={14} />}
            onClick={onClear}
            disabled={disabled}
          >
            {t("translation.clear")}
          </Button>
        )}
        <Text
          size="xs"
          c={overLimit ? "red" : nearLimit ? "brand.7" : "dimmed"}
          fw={overLimit ? 600 : 400}
        >
          {count.toLocaleString()} / {MAX_INPUT_CHARS.toLocaleString()}
        </Text>
      </Group>

      {overLimit && (
        <Text size="xs" c="red">
          {t("translation.overLimit", { max: MAX_INPUT_CHARS.toLocaleString() })}
        </Text>
      )}
    </Stack>
  );
};
